/**
 * {@link DataTable} for field event summaries.
 */
import * as React from 'react';
import { Calendar } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { Button } from '@/components/forms/Button';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { EventSummaryRow } from './EventSummaryRow';
import { EventSummaryCard } from './EventSummaryCard';
import { EventSummaryFiltersContent } from './EventSummaryFilters';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import {
  getEventSummaries,
  getEventSummary,
  exportEventSummariesExcel,
} from '@/services/event-summaries.service';
import { usePageActions } from '@/hooks/usePageActions';
import { cn } from '@/lib/utils';
import {
  DEFAULT_EVENT_SUMMARY_FILTERS,
  ALL_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS,
} from './event-summary-configs';
import {
  buildListParams,
  hasEventSummaryActiveFilters,
  resolveDateRange,
} from './event-summary-table-utils';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { EventSummary, EventSummaryFilters } from '@/types/event-summary.types';
/** Props for {@link EventSummariesDataTable}. */
interface EventSummariesDataTableProps {
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditSummary?: (summaryId: string) => void;
  onViewSummary?: (summaryId: string) => void;
  enableFilters?: boolean;
  defaultFilters?: EventSummaryFilters;
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  toolbarLeft?: React.ReactNode;
  /** Extra nodes rendered at the start of the section-actions bar (before filter/export/create). */
  toolbarExtra?: React.ReactNode;
  pageActions?: { canEditRow?: boolean; canViewRow?: boolean; canExportCsv?: boolean };
  deepLinkEventId?: string | null;
}

/** {@link DataTable} for field event summaries. */
export const EventSummariesDataTable: React.FC<EventSummariesDataTableProps> = ({
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditSummary,
  onViewSummary,
  enableFilters = true,
  defaultFilters = DEFAULT_EVENT_SUMMARY_FILTERS,
  layoutWidth: layoutWidthProp = 'full',
  isSection = false,
  toolbarLeft,
  toolbarExtra,
  pageActions: externalPageActions,
  deepLinkEventId = null,
}) => {
  const pageActionsFromHook = usePageActions('event-summaries');
  const pageActions = externalPageActions || pageActionsFromHook;
  const showExport = pageActions.canExportCsv === true;
  const resolvedView = onViewSummary ?? onToggleRow;
  const [isExporting, setIsExporting] = React.useState(false);

  const dt = useDataTable<EventSummaryFilters, string>({
    tableName: 'event-summaries',
    defaultFilters,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth: layoutWidthProp,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const { dateFrom, dateTo } = resolveDateRange(dt.filters);

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['event-summaries', dt.currentPage, dt.itemsPerPage, dt.filters, deepLinkEventId],
    queryFn: () =>
      getEventSummaries(
        buildListParams(dt.filters, {
          page: dt.currentPage,
          pageSize: dt.itemsPerPage,
          eventId: deepLinkEventId,
          dateFrom,
          dateTo,
        }),
      ),
  });

  const data = apiResponse?.data || [];
  const totalItems = apiResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  /** Deep-link / expand of a row not on the current page — fetch that summary alone. */
  const {
    data: expandedSummaryData,
    isLoading: isExpandedSummaryLoading,
    isError: isExpandedSummaryError,
    error: expandedSummaryError,
  } = useQuery({
    queryKey: ['event-summary', expandedRowId],
    queryFn: () => getEventSummary(expandedRowId!),
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedSummaryError,
    error: expandedSummaryError,
    catalogKey: 'eventSummaryNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  /** Use only the fetched data. */
  const expandedSummary = expandedSummaryData ?? null;

  const hasActiveFilters = React.useMemo(
    () => hasEventSummaryActiveFilters(dt.filters, defaultFilters),
    [dt.filters, defaultFilters],
  );

  /** Toggle one status chip and reset pagination. */
  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status as any)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status as any],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Period chip change — reset to page 1. */
  const setDateRange = React.useCallback(
    (range: EventSummaryFilters['dateRange']) => {
      dt.setFilters((prev) => ({ ...prev, dateRange: range }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Custom from/to — reset to page 1. */
  const setCustomDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({ ...prev, customDateFrom: from, customDateTo: to }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Single establishment (or clear) — reset to page 1. */
  const setEstablishmentFilter = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, establishmentIds: id ? [id] : [] }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <EventSummaryFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleStatus={toggleStatusFilter}
        onSetDateRange={setDateRange}
        onSetCustomDateRange={setCustomDateRange}
        onSetEstablishment={setEstablishmentFilter}
      />
    ),
    [
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      toggleStatusFilter,
      setDateRange,
      setCustomDateRange,
      setEstablishmentFilter,
    ],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  /** Export the current filter set (not only the visible page). */
  const handleExportExcel = React.useCallback(async () => {
    if (totalItems === 0) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportEventSummariesExcel({
        eventId: deepLinkEventId,
        statuses: dt.filters.statuses.length > 0 ? dt.filters.statuses : undefined,
        establishmentIds:
          dt.filters.establishmentIds && dt.filters.establishmentIds.length > 0
            ? dt.filters.establishmentIds
            : undefined,
        dateFrom,
        dateTo,
        search: undefined,
      });
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [dt.filters, dateFrom, dateTo, deepLinkEventId, totalItems]);

  const isSummaryDetailLoading = !!expandedRowId && (isExpandedSummaryLoading || !expandedSummary);

  /** Side / expanded panel: event date + content, or a loading placeholder. */
  const renderSummaryDetailsPanel = React.useCallback(() => {
    if (expandedSummary) {
      return (
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>
              {expandedSummary.event?.eventDate
                ? new Date(expandedSummary.event.eventDate).toLocaleDateString('fr-FR')
                : '—'}
            </span>
          </div>
          <div>
            <div className="text-sm font-medium text-muted-foreground mb-1">Avis</div>
            <div className="text-sm text-foreground whitespace-pre-wrap">
              {expandedSummary.content || (
                <span className="italic text-muted-foreground">Aucun contenu</span>
              )}
            </div>
          </div>
        </div>
      );
    }
    if (isSummaryDetailLoading) {
      return <div className="p-4 text-sm text-muted-foreground">Chargement…</div>;
    }
    return null;
  }, [expandedSummary, isSummaryDetailLoading]);

  return (
    <DataTable<EventSummary>
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="synthèse(s)"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={6}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      toolbarActions={
        enableFilters || showExport ? (
          <>
            {enableFilters && (
              <Button
                preset="toolbar-filter"
                className={cn(dt.showFilters && 'section-action-button-active')}
                showBadge={hasActiveFilters}
                onClick={() => dt.setShowFilters((prev) => !prev)}
              />
            )}
            {showExport && (
              <Button
                preset="toolbar-export"
                onClick={handleExportExcel}
                loading={isExporting}
                disabled={totalItems === 0}
                tooltipText={
                  totalItems === 0
                    ? getErrorMessage('exportEmpty')
                    : 'Exporter la liste des synthèses en Excel'
                }
              />
            )}
          </>
        ) : undefined
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={dt.clearFilters}
            resultCount={totalItems}
            resultLabel="synthèse(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn
                          ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                          : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                      )}
                    >
                      {FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Événement</th>
          <th>Établissement</th>
          <th>Activité</th>
          <th>Statut</th>
          <th>Date de saisie</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(summary) => (
        <EventSummaryRow
          summary={summary}
          onView={resolvedView}
          onEdit={onEditSummary ? () => onEditSummary(summary.id) : undefined}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={() => renderSummaryDetailsPanel()}
      renderCard={(summary) => (
        <EventSummaryCard
          summary={summary}
          onView={resolvedView}
          onEdit={onEditSummary ? () => onEditSummary(summary.id) : undefined}
          pageActions={pageActions}
        />
      )}
      emptyIcon={Calendar}
      emptyText="Aucune synthèse trouvée"
      title="Synthèses"
      sidebarMenuId="menu-events"
      isSection={isSection}
      toolbarLeft={toolbarLeft}
      toolbarExtra={toolbarExtra}
      expandedItem={expandedSummary}
      isExpandedDetailLoading={isExpandedSummaryLoading}
      isDetailsPanelOpen={!!expandedRowId}
      onCloseDetails={onCloseDetails}
      renderDetailsPanel={renderSummaryDetailsPanel}
    />
  );
};
